const CLASSIFICATION_KEY_LABELS: Record<string, string> = {
  category: 'Category',
  moscowCategory: 'MoSCoW',
  paretoBand: 'Pareto',
  pyramidCategory: 'Pyramid',
  timeHorizon: 'Horizon',
  strategicTheme: 'Theme',
  quadrant: 'Quadrant',
  urgency: 'Urgency',
  importance: 'Importance',
};

const QUADRANT_VALUE_LABELS: Record<string, string> = {
  ui: 'Urgent + Important',
  in: 'Important + Not Urgent',
  un: 'Urgent + Not Important',
  nn: 'Neither Urgent nor Important',
};

export const CLASSIFICATION_CHIP_CLASS =
  'inline-flex rounded-full bg-[var(--hover-tint)] px-2 py-0.5 text-xs text-[var(--text-sec)] border border-[var(--rule)]';

export const SOURCE_CONTEXT_CHIP_CLASS =
  'inline-flex rounded-full bg-[var(--surface)] px-2 py-0.5 text-xs text-[var(--text-sec)] border border-[var(--rule)]';

export const CHIP_OVERFLOW_CLASS =
  'inline-flex rounded-full bg-[var(--surface)] px-2 py-0.5 text-xs text-[var(--text-ter)] border border-[var(--rule)]';

function toTitleCase(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function formatClassificationLabel(key: string): string {
  return CLASSIFICATION_KEY_LABELS[key] ?? toTitleCase(key);
}

export function formatClassificationValue(key: string, value: string): string {
  if (key === 'quadrant') {
    return QUADRANT_VALUE_LABELS[value] ?? toTitleCase(value);
  }

  if (key === 'category' && value in QUADRANT_VALUE_LABELS) {
    return QUADRANT_VALUE_LABELS[value];
  }

  return toTitleCase(value);
}

export function getVisibleClassificationEntries(
  classifications?: Record<string, string>
): Array<{ key: string; label: string; value: string }> {
  if (!classifications) {
    return [];
  }

  let entries = Object.entries(classifications).filter(
    (entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1].trim().length > 0
  );

  if (entries.length > 1) {
    entries = entries.filter(([key]) => key !== 'category');
  }

  return entries.map(([key, value]) => ({
    key,
    label: formatClassificationLabel(key),
    value: formatClassificationValue(key, value),
  }));
}

export function buildClassificationSummaryTags(
  classificationsByItem?: Record<string, Record<string, string>>
): string[] {
  if (!classificationsByItem) {
    return [];
  }

  const groupedValues = new Map<string, { label: string; values: string[] }>();

  Object.values(classificationsByItem).forEach((classifications) => {
    getVisibleClassificationEntries(classifications).forEach((entry) => {
      const existing = groupedValues.get(entry.key);
      if (!existing) {
        groupedValues.set(entry.key, { label: entry.label, values: [entry.value] });
        return;
      }

      if (!existing.values.includes(entry.value)) {
        existing.values.push(entry.value);
      }
    });
  });

  return Array.from(groupedValues.values())
    .filter((entry) => entry.values.length > 0)
    .slice(0, 3)
    .map((entry) => {
      const visibleValues = entry.values.slice(0, 3);
      const remainder = entry.values.length - visibleValues.length;
      return `${entry.label}: ${visibleValues.join(', ')}${remainder > 0 ? ` +${remainder}` : ''}`;
    });
}
